import Papa from 'papaparse';
import readXlsxFile from 'read-excel-file/browser';
import writeXlsxFile from 'write-excel-file/browser';

export const SPREADSHEET_ACCEPT = '.xlsx,.csv';
export const SPREADSHEET_MAX_IMPORT_BYTES = 10 * 1024 * 1024;

const DANGEROUS_OBJECT_KEYS = new Set(['__proto__', 'prototype', 'constructor']);
const EXCEL_FORMULA_PREFIX = /^[=+\-@]/;
const APOSTROPHE_VARIANTS_REGEX = /['`’‘ʼʻ]/g;
const HEADER_KEY_ALIASES = {
  'hemis id': 'hemis_id',
  hemisid: 'hemis_id',
  hemis: 'hemis_id',
  'talaba id': 'hemis_id',
  talabaid: 'hemis_id',
  'student id': 'hemis_id',
  studentid: 'hemis_id',
  fio: 'full_name',
  fish: 'full_name',
  'f i sh': 'full_name',
  'f i o': 'full_name',
  'full name': 'full_name',
  fullname: 'full_name',
  'toliq ismi': 'full_name',
  studentname: 'full_name',
  'student name': 'full_name',
  familya: 'familya',
  familiya: 'familya',
  surname: 'familya',
  'last name': 'familya',
  ism: 'ism',
  name: 'ism',
  'first name': 'ism',
  sharif: 'sharif',
  'otasi ismi': 'sharif',
  fuqarolik: 'citizenship',
  fuqaroligi: 'citizenship',
  citizenship: 'citizenship',
  millat: 'nationality',
  millati: 'nationality',
  nationality: 'nationality',
  ethnicity: 'nationality',
  davlat: 'republic',
  country: 'republic',
  viloyat: 'region',
  region: 'region',
  tuman: 'district',
  district: 'district',
  jins: 'gender',
  jinsi: 'gender',
  gender: 'gender',
  sex: 'gender',
  'tugilgan sana': 'birth_date',
  'birth date': 'birth_date',
  'date of birth': 'birth_date',
  kurs: 'course',
  course: 'course',
  fakultet: 'faculty',
  faculty: 'faculty',
  guruh: 'group',
  group: 'group',
  'talim tili': 'education_language',
  'education language': 'education_language',
  'study language': 'education_language',
  'oquv yili': 'study_year',
  'study year': 'study_year',
  'academic year': 'study_year',
  mutaxassislik: 'speciality',
  mutaxasisligi: 'speciality',
  specialty: 'speciality',
  speciality: 'speciality',
  'talim turi': 'education_type',
  degree: 'education_type',
  level: 'education_type',
  'talim shakli': 'education_form',
  'education form': 'education_form',
  'study form': 'education_form',
  'tolov shakli': 'payment_form',
  'payment form': 'payment_form',
  'contract type': 'payment_form',
  jshshir: 'jshshir',
  pinfl: 'jshshir',
  passport: 'passport',
  pasport: 'passport',
  telefon: 'phone',
  phone: 'phone',
  'phone number': 'phone'
};
const HEADER_ALIASES = new Set(Object.keys(HEADER_KEY_ALIASES));

const getFileExtension = fileName =>
  String(fileName || '')
    .trim()
    .toLowerCase()
    .split('.')
    .pop();

const sanitizeHeader = value => {
  const header = String(value || '').trim();

  if (!header || DANGEROUS_OBJECT_KEYS.has(header)) {
    return '';
  }

  return header;
};

const normalizeHeaderAlias = value =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(APOSTROPHE_VARIANTS_REGEX, '')
    .replace(/\s+/g, ' ')
    .replace(/[_-]+/g, ' ');

const canonicalizeHeader = value => {
  const fallbackHeader = sanitizeHeader(value);

  if (!fallbackHeader) {
    return '';
  }

  const normalized = normalizeHeaderAlias(fallbackHeader);
  return HEADER_KEY_ALIASES[normalized] || fallbackHeader;
};

const normalizeCellValue = value => {
  if (value === null || value === undefined) {
    return '';
  }

  if (value instanceof Date) {
    return value.toISOString().slice(0, 10);
  }

  if (Array.isArray(value)) {
    return value.map(normalizeCellValue).join(' ');
  }

  if (typeof value === 'object') {
    if (typeof value.text === 'string') {
      return value.text;
    }

    if (Array.isArray(value.richText)) {
      return value.richText.map(chunk => String(chunk?.text || '')).join('');
    }

    if (value.result !== undefined && value.result !== null) {
      return normalizeCellValue(value.result);
    }

    if (typeof value.hyperlink === 'string') {
      return typeof value.text === 'string' && value.text !== '' ? value.text : value.hyperlink;
    }
  }

  return String(value);
};

const hasMeaningfulCellValue = value => normalizeCellValue(value).trim() !== '';

const sanitizeImportedRow = rawRow => {
  const safeRow = {};
  let hasValue = false;

  Object.keys(rawRow || {}).forEach(key => {
    const safeKey = canonicalizeHeader(key);

    if (!safeKey) {
      return;
    }

    const safeValue = normalizeCellValue(rawRow[key]);
    safeRow[safeKey] = safeValue;

    if (safeValue !== '') {
      hasValue = true;
    }
  });

  return hasValue ? safeRow : null;
};

const readFileAsText = file =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = event => resolve(String(event.target?.result || ''));
    reader.onerror = () => reject(new Error("Faylni o'qib bo'lmadi."));
    reader.readAsText(file, 'utf-8');
  });

const readXlsxRows = async file => {
  const workbook = await readXlsxFile(file);
  const sheets = Array.isArray(workbook) ? workbook : [];
  const candidates = [];

  const parseSheetRows = sheetRows => {
    const rows = Array.isArray(sheetRows) ? sheetRows : [];

    if (!rows.length) {
      return { parsedRows: [], score: -1 };
    }

    const nonEmptyRowIndexes = rows.reduce((indexes, row, index) => {
      if (Array.isArray(row) && row.some(hasMeaningfulCellValue)) {
        indexes.push(index);
      }
      return indexes;
    }, []);

    if (!nonEmptyRowIndexes.length) {
      return { parsedRows: [], score: -1 };
    }

    let headerRowIndex = nonEmptyRowIndexes[0];
    let headerScore = -1;

    nonEmptyRowIndexes.slice(0, 10).forEach(rowIndex => {
      const row = rows[rowIndex] || [];
      const score = row.reduce((count, cell) => {
        const normalized = normalizeHeaderAlias(cell);
        return HEADER_ALIASES.has(normalized) ? count + 1 : count;
      }, 0);

      if (score > headerScore) {
        headerScore = score;
        headerRowIndex = rowIndex;
      }
    });

    const rawHeaders = Array.isArray(rows[headerRowIndex]) ? rows[headerRowIndex] : [];
    const headers = rawHeaders.map(canonicalizeHeader);

    if (!headers.some(Boolean)) {
      return { parsedRows: [], score: headerScore };
    }

    const parsedRows = [];

    rows.slice(headerRowIndex + 1).forEach(rawRow => {
      if (!Array.isArray(rawRow) || !rawRow.some(hasMeaningfulCellValue)) {
        return;
      }

      const nextRow = {};
      let hasValue = false;

      headers.forEach((header, index) => {
        if (!header) {
          return;
        }

        const cellValue = normalizeCellValue(rawRow[index]);
        nextRow[header] = cellValue;

        if (cellValue !== '') {
          hasValue = true;
        }
      });

      if (hasValue) {
        parsedRows.push(nextRow);
      }
    });

    return { parsedRows, score: headerScore };
  };

  sheets.forEach(sheet => {
    const parsed = parseSheetRows(sheet?.data);
    if (parsed.parsedRows.length > 0) {
      candidates.push(parsed);
    }
  });

  if (!candidates.length) {
    return [];
  }

  candidates.sort((left, right) => {
    if (right.score !== left.score) {
      return right.score - left.score;
    }

    return right.parsedRows.length - left.parsedRows.length;
  });

  return candidates[0].parsedRows;
};

const readCsvRows = async file => {
  const csvText = await readFileAsText(file);
  const parsed = Papa.parse(csvText, {
    header: true,
    skipEmptyLines: 'greedy',
    transformHeader: canonicalizeHeader
  });

  if (parsed.errors?.length) {
    throw new Error(parsed.errors[0].message || "CSV faylni o'qishda xatolik yuz berdi.");
  }

  return (parsed.data || [])
    .map(sanitizeImportedRow)
    .filter(Boolean);
};

const protectExportValue = value => {
  if (value === null || value === undefined) {
    return '';
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }

  if (value instanceof Date) {
    return value;
  }

  const stringValue = String(value);
  const leftTrimmed = stringValue.replace(/^\s+/, '');

  if (EXCEL_FORMULA_PREFIX.test(leftTrimmed)) {
    return `'${stringValue}`;
  }

  return stringValue;
};

const normalizeSheetName = value =>
  String(value || 'Sheet1')
    .replace(/[\\/*?:[\]]/g, ' ')
    .trim()
    .slice(0, 31) || 'Sheet1';

const collectColumnHeaders = rows => {
  const headers = [];
  const seen = new Set();

  (rows || []).forEach(row => {
    Object.keys(row || {}).forEach(key => {
      const safeKey = sanitizeHeader(key);

      if (!safeKey || seen.has(safeKey)) {
        return;
      }

      seen.add(safeKey);
      headers.push(safeKey);
    });
  });

  return headers;
};

export const readSpreadsheetRows = async file => {
  if (!file) {
    throw new Error('Import fayl tanlanmagan.');
  }

  if (file.size > SPREADSHEET_MAX_IMPORT_BYTES) {
    throw new Error('Import fayl hajmi 10 MB dan oshmasligi kerak.');
  }

  const extension = getFileExtension(file.name);

  if (extension === 'csv') {
    return readCsvRows(file);
  }

  if (extension === 'xlsx') {
    return readXlsxRows(file);
  }

  throw new Error("Xavfsizlik sababli faqat .xlsx yoki .csv fayl qabul qilinadi.");
};

export const exportRowsToXlsx = async ({ rows, fileName, sheetName }) => {
  const exportRows = Array.isArray(rows) ? rows : [];

  if (!exportRows.length) {
    return;
  }

  const headers = collectColumnHeaders(exportRows);
  const data = [
    headers.map(header => ({
      value: header,
      fontWeight: 'bold'
    })),
    ...exportRows.map(row =>
      headers.map(header => ({
        value: protectExportValue(row?.[header])
      }))
    )
  ];

  await writeXlsxFile(data, {
    fileName,
    sheet: normalizeSheetName(sheetName)
  });
};
